/**
 * Plan tiers, per-unit rates, and onboarding-path config. Consumed by the
 * billing usage ledger for cycle-reset allocations and by the customer-facing
 * calculator + Settings surfaces for plan comparison.
 */
export const PLAN_TIERS = [
    {
        id: 'starter',
        label: 'Starter',
        priceUsd: 150,
        includedMinutes: 250,
        includedSms: 125,
        perMinuteUsd: 0.5,
        perSmsUsd: 0.2,
        doorBracketMax: 250,
        features: {
            activityLogRetentionDays: 30,
            activityReporting: 'basic',
            supportLevel: 'community',
            sdkAccess: true,
            mcpDataAccess: false,
            realtimeWebhooks: false,
            builderProgramUpdates: false,
        },
    },
    {
        id: 'growth',
        label: 'Growth',
        priceUsd: 250,
        includedMinutes: 500,
        includedSms: 250,
        perMinuteUsd: 0.4,
        perSmsUsd: 0.2,
        doorBracketMax: 500,
        features: {
            activityLogRetentionDays: 90,
            activityReporting: 'analytics',
            supportLevel: 'email',
            sdkAccess: true,
            mcpDataAccess: false,
            realtimeWebhooks: false,
            builderProgramUpdates: false,
        },
    },
    {
        id: 'scale',
        label: 'Scale',
        priceUsd: 400,
        includedMinutes: 1000,
        includedSms: 500,
        perMinuteUsd: 0.3,
        perSmsUsd: 0.2,
        doorBracketMax: 1200,
        features: {
            activityLogRetentionDays: 365,
            activityReporting: 'analytics',
            supportLevel: 'priority',
            sdkAccess: true,
            mcpDataAccess: false,
            realtimeWebhooks: false,
            builderProgramUpdates: false,
        },
    },
    {
        id: 'pro',
        label: 'Pro',
        priceUsd: 525,
        includedMinutes: 1500,
        includedSms: 750,
        perMinuteUsd: 0.25,
        perSmsUsd: 0.2,
        doorBracketMax: 3000,
        features: {
            activityLogRetentionDays: 365,
            activityReporting: 'custom',
            supportLevel: 'priority',
            sdkAccess: true,
            mcpDataAccess: true,
            realtimeWebhooks: true,
            builderProgramUpdates: true,
        },
    },
    {
        id: 'enterprise',
        label: 'Enterprise',
        priceUsd: 900,
        includedMinutes: 3000,
        includedSms: 1500,
        perMinuteUsd: 0.2,
        perSmsUsd: 0.2,
        doorBracketMax: null,
        features: {
            activityLogRetentionDays: -1,
            activityReporting: 'custom',
            supportLevel: 'dedicated',
            sdkAccess: true,
            mcpDataAccess: true,
            realtimeWebhooks: true,
            builderProgramUpdates: true,
        },
    },
];
export const ONBOARDING_FEE_STANDARD_PATH_MULTIPLIER = 0.5;
export const BUILDER_PATH_TRIAL_DAYS = 5;
export const DEFAULT_PLAN_TIER_ID = 'starter';
/** Runtime tuple of every plan tier id, for validators (`@IsIn(...)`). */
export const PLAN_TIER_IDS = [
    'starter',
    'growth',
    'scale',
    'pro',
    'enterprise',
];
export function getPlanTier(id) {
    const tier = PLAN_TIERS.find(t => t.id === id);
    if (!tier)
        throw new Error(`Unknown plan tier: ${id}`);
    return tier;
}
export function recommendPlanTierByDoors(doorCount) {
    for (const tier of PLAN_TIERS) {
        if (tier.doorBracketMax === null || doorCount <= tier.doorBracketMax) {
            return tier;
        }
    }
    return PLAN_TIERS[PLAN_TIERS.length - 1];
}
/**
 * Property-management call-length average from historical BQ analysis.
 * Used by the onboarding estimator to convert calls-per-month to minutes.
 */
export const AVERAGE_MINUTES_PER_CALL = 1.8;
/**
 * Compute the total projected monthly bill for a tier at a given volume,
 * base plus per-unit overage for whatever spills past the included bundle.
 */
export function projectedMonthlyCost(tier, minutes, sms) {
    const minutesOver = Math.max(0, minutes - tier.includedMinutes);
    const smsOver = Math.max(0, sms - tier.includedSms);
    return (tier.priceUsd + minutesOver * tier.perMinuteUsd + smsOver * tier.perSmsUsd);
}
/**
 * How far above the largest tier's included bundle we still let the
 * estimator recommend Enterprise + overage. Beyond this factor for either
 * minutes or SMS, the volume genuinely wants a bespoke sales quote.
 */
export const CONTACT_SALES_HEADROOM_FACTOR = 1.2;
/**
 * Return the tier with the lowest projected monthly bill for the caller's
 * volume — the base fee plus per-unit overage for whatever spills past the
 * included bundle. Overage at a smaller tier's rate can genuinely beat the
 * next tier's higher base, so we compare projected totals, not bundle fit.
 * Only returns null well past the largest tier on either axis (see the
 * headroom factor); a light overage on Enterprise is still a valid
 * recommendation.
 */
export function estimatePlanTierByUsage(input) {
    const largest = PLAN_TIERS[PLAN_TIERS.length - 1];
    const minutesCap = largest.includedMinutes * CONTACT_SALES_HEADROOM_FACTOR;
    const smsCap = largest.includedSms * CONTACT_SALES_HEADROOM_FACTOR;
    if (input.minutesEstimate > minutesCap || input.smsEstimate > smsCap) {
        return null;
    }
    let best = null;
    for (const tier of PLAN_TIERS) {
        const monthlyCost = projectedMonthlyCost(tier, input.minutesEstimate, input.smsEstimate);
        if (!best || monthlyCost < best.monthlyCost) {
            best = { tier, monthlyCost };
        }
    }
    return best;
}
//# sourceMappingURL=plan.js.map